<template>
  <div>
    <ViewHeader
      :title="$store.state.quarter?.display_name"
      class="text-capitalize"
      compact
    >
      <CButton
        v-if="isAdministrator && currentTab === 'Dossiers'"
        class="ra-button"
        color="primary"
        @click="triggerCalculations"
        :disabled="recalculateTriggered"
      >
        Recalculate
      </CButton>
    </ViewHeader>
    <div class="d-flex justify-content-between align-items-end mb-2">
      <MzTabs v-model="currentTab" :tabs="tabs"/>
      <CButton
        v-if="currentTab !== 'Monitoring'"
        class="ra-button"
        color="secondary"
        size="sm"
        @click="
          () =>
          $downloadFile({
            url: ENTITY_ENDPOINTS.export,
            query: periodQuery
          })
        "
        v-c-tooltip="'Download export file'"
      >
        Export data
      </CButton>
    </div>
    <div v-if="currentTab === 'Dossiers'">
      <tableComponent
        class="period-table"
        ref="periodTable"
        force-filter
        :filter-exclude-columns="['status','actions']"
        :init-filters="defaultEntityCodeFilter"
        :url="ENTITY_ENDPOINTS.main + 'quarter'"
        :columns="columns"
        :headings="headings"
        :export-url="ENTITY_ENDPOINTS.export"
        :display-columns-selector="false"
      >
        <template #status="{ row }">
          Dossier {{ !row.closed_periods.includes(quarter_name) ? 'en cours' : 'clôturé'}}
        </template>
        <template #completed_cycles="{ row }">
          <div
            class="ra-tag"
            :class="getCompletedCyclesColorClass(getCompletedCycles(row)?.length || 0, cycleNames.length)"
            v-c-tooltip="{content: getCompletedCyclesTooltip(getCompletedCycles(row)), html: true}"
          >
            {{ getCompletedCycles(row)?.length || 0 }} / {{ cycleNames.length }}
          </div>
        </template>
        <template #period="{ row }">
          {{ quarter_name }}
        </template>
        <template #dossier="{ row }">
          {{ row.code }}_{{ quarter_name.replace('-', '_') }}
        </template>
        <template #name="{ row }">
          <CLink
            :to="{ name: 'Entity Details', params: { entity_id: row.code } }"
          >
            {{ row.name }}
          </CLink>
        </template>
        <template #last_update_date="{ row }">
          {{ $formatDate(row.last_update_date[quarter_name], "YYYY-MM-DD HH:mm:SS") }}
        </template>
        <template #test_results="{ row }">
          {{ getNumberOfRemainingTests(row) }}
        </template>
        <template #actions="{ row }">
          <div class="d-flex gap-2 align-items-center">
            <CButton
              color="primary"
              size="sm"
              class="ra-button"
              @click="() => updateClosedPeriods(row)"
            >
              <div class="d-flex justify-content-between">
                <template v-if="row.closed_periods.includes(quarter_name)">
                  Open
                  <i class="fa fa-key ml-1"/>
                </template>
                <template v-else>
                  Close
                  <i class="fa fa-lock-open ml-1"/>
                </template>
              </div>
            </CButton>
            <CButton
              v-if="isAdministrator && !row.closed_periods.includes(quarter_name)"
              color="secondary"
              size="sm"
              class="ra-button"
              @click="triggerEntityCalculations(row.code)"
            >
              Recalculate
            </CButton>
          </div>
        </template>
      </tableComponent>
    </div>
    <div v-if="currentTab === 'Monitoring'">
      <tableComponent
        v-if="filesMeta && filesMeta.length"
        default-order-by="-uploaded_at"
        :filterExcludeColumns="filesColumns"
        :columns="filesColumns"
        :data="filesMeta"
        :headings="headings"
        :export-url="CLOSING_FILE_ENDPOINTS.export_main"
        displayButtonsRow
      >
        <template #export_btns>
          <CButton
            @click="handleSynchronize"
            class="ra-button mr-3"
            size="sm"
            color="primary"
          >
            Synchronize
          </CButton>
          <CButton
            class="ra-button"
            color="secondary"
            size="sm"
            @click="
              () =>
              $downloadFile({
                url: CLOSING_FILE_ENDPOINTS.export_main,
                query: { quarter_name },
              })
            "
            v-c-tooltip="'Download export file'"
          >
            Export data
          </CButton>
        </template>
        <template #nb_rows="{ row }">
          {{ $getNumericalValue(row.nb_rows, 0) }}
        </template>
        <template #nb_columns="{ row }">
          {{ $getNumericalValue(row.nb_columns, 0) }}
        </template>
      </tableComponent>
      <div v-else class="alert alert-danger">
        Sync not performed, {{ currentTab }} not available
      </div>
    </div>
  </div>
</template>

<script>
import {CYCLES} from "@/constants/cycle";

import { ClosingFileService, CLOSING_FILE_ENDPOINTS } from "@/services/closing_file";
import { EntitiesService, ENTITY_ENDPOINTS } from "@/services/entity";
import { ActiveUser } from "@/services/user";
import { TaskService } from "@/services/task";

export default {
  name: "QuarterDetails",
  data() {
    return {
      ClosingFileService,
      TaskService,
      ENTITY_ENDPOINTS,
      CLOSING_FILE_ENDPOINTS,
      tabs: ["Dossiers", "Monitoring"],
      currentTab: "Dossiers",
      filesMeta: [],
      filesColumns: [
        "file_name",
        "size",
        "uploaded_at",
        "nb_rows",
        "nb_columns",
      ],
      dossierData: [],
      columns: [
        "dossier",
        "code",
        "name",
        "group",
        "test_results",
        "last_update_date",
        "completed_cycles",
        "status",
        "actions",
      ],
      headings: {
        code: "Entité",
        name: "Nom",
        group: "Département financier",
        test_results: "Nombre de contrôles KO",
        last_update_date: "Dernière mise à jour des données",
        completed_cycles: "Nombre de cycles mis à jour",
        status: "Statut",
        actions: "Action",
        uploaded_at: "Date d'upload",
        size: "Taille (Mo)",
        nb_rows: "Nombre de lignes",
        file_name: "Nom du fichier",
        nb_columns: "Nombre de colonnes",
      },
      recalculateTriggered: false,
    };
  },
  computed: {
    defaultEntityCodeFilter(){
      return {
        quarter_name: this.quarter_name,
        'search_code__nin': [
          'ALL',
          'DFF',
          'OFFSHORE',
          'CORPORATE',
          'LUXEL',
          'EGM',
        ]
      }
    },
    periodQuery() {
      return {
        quarter: this.quarter,
        ...this.$refs.periodTable.getUrlParams().urlParams,
        'search_code__nin': JSON.stringify(this.defaultEntityCodeFilter['search_code__nin'])
      }
    },
    cycleNames(){
      return Object.values(CYCLES)
    },
    quarter_name(){
      return this.$route.params.quarter_name
    },
    filterExcludeColumns(){
      const allowedFilterCols = [
        'code',
        'name',
        'group',
      ];
      return this.columns.filter(col => !allowedFilterCols.includes(col))
    },
    isAdministrator() {
      return ActiveUser.isAdministrator();
    }
  },
  async mounted() {
    await this.fetchSapMonitoring();
  },
  methods: {
    async handleSynchronize() {
      const data = await ClosingFileService.synchronize(this.quarter_name)
      if (data?.task_id) {
        alert("Synchronize initiated", {color: "success"});
      } else {
        alert("Synchronize failed", {color: "danger"});
      }
    },
    getNumberOfRemainingTests(row){
      return 31 - this.getNumberOfOkTests(row);
    },
    getNumberOfOkTests(row){
      return Object.values(row.test_results?.[this.quarter_name] || {}).filter(testOk => testOk)?.length || 0
    },
    getCompletedCyclesColorClass(completedCycles, totalCycles){
      if (completedCycles === totalCycles) return 'success';
      if (completedCycles === 0) return 'secondary';
      return 'primary'
    },
    async fetchSapMonitoring() {
      this.filesMeta = await ClosingFileService.getMeta(this.quarter_name);
    },
    getCompletedCyclesTooltip(completedCycles){
      const cycleNames = completedCycles.map(cycle => CYCLES[cycle])
      return cycleNames.join('<br />')
    },
    getCompletedCycles(entityData){
      return entityData.completed_cycles?.[this.quarter_name] || []
    },
    async updateClosedPeriods(entityData) {
      const closedPeriods = new Set(entityData.closed_periods || []);
      if (closedPeriods.has(this.quarter_name)) closedPeriods.delete(this.quarter_name);
      else closedPeriods.add(this.quarter_name);
      await EntitiesService.editOrCreateEntity(entityData.code, {closed_periods: Array.from(closedPeriods)});
      this.$refs.periodTable.update()
    },
    async triggerCalculations() {
      this.recalculateTriggered = true;
      const data = await ClosingFileService.runQuarterCalculation(this.quarter_name);
      if (data?.task_id) {
        alert("Recalculate initiated", { color: "success" });
      } else {
        alert("Recalculate failed", { color: "danger" });
        this.recalculateTriggered = false;
      }
    },
    async triggerEntityCalculations(entity_code) {
      const task_id = await ClosingFileService.runEntityCalculations(entity_code, this.quarter_name)
    }
  },
};
</script>

<style lang="scss">
.period-table {
  thead tr {
    th:nth-child(-n+2) {
      min-width: min-content !important;
      max-width: max-content !important;
    }
  }
}

.ra-input.input-sm.filter-input .input-group-wrapper {
  min-width: 140px !important;
}

.ra-table .buttons-row {
  display: flex !important;
  justify-content: flex-end;
}

.number-of-records {
  display: none !important;
}
</style>
